import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, CheckCircle2, ClipboardList, CloudUpload, Copy, Download, Eye, FileArchive, FileText, Image as ImageIcon, Plus, RefreshCw, ScanSearch, Server, Trash2, Trophy, UserRound, XCircle } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageTitle, Textarea } from '../components/ui'
import { api } from '../lib/api'
import type { AgentAsset, AgentAssetDetail, AssetDownloadInfo } from '../lib/types'

export function AssetsPage() {
  const qc = useQueryClient()
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api<AgentAsset[]>('/api/v1/assets') })
  const [open, setOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [notice, setNotice] = useState('')

  async function sync(asset: AgentAsset) {
    setSyncing(asset.id); setNotice('')
    try {
      await api(`/api/v1/assets/${asset.id}/sync`, { method: 'POST' })
      setNotice(`「${asset.name}」已同步到 Mixer`)
      await qc.invalidateQueries({ queryKey: ['assets'] })
    } catch (error) { setNotice(error instanceof Error ? error.message : '同步失败') } finally { setSyncing(null) }
  }

  async function removeAsset(asset: AgentAsset) {
    // Native confirm matches the project-wide pattern (see EvaluationsPage /
    // ConsolePage). Deletion is irreversible: the uploaded packages, score
    // history and linked submission are all wiped.
    if (!confirm(`确认删除资产「${asset.name}」？上传的源码包、评分记录等关联信息将被永久移除，此操作不可恢复。`)) return
    setDeleting(asset.id); setNotice('')
    try {
      await api(`/api/v1/assets/${asset.id}`, { method: 'DELETE' })
      setNotice(`「${asset.name}」已删除`)
      setDetailId(null)
      await qc.invalidateQueries({ queryKey: ['assets'] })
    } catch (error) { setNotice(error instanceof Error ? error.message : '删除失败') } finally { setDeleting(null) }
  }

  return <div className="workspace-page">
    <PageTitle eyebrow="AGENT & MCP ASSETS" title="资产库" description="上传、托管和评测使用同一份 Agent/MCP 资产。" actions={<Button onClick={() => setOpen(true)}><Plus size={15}/>添加外部 MCP</Button>}/>
    {notice && <div className="evaluation-notice">{notice}</div>}
    {assets.isLoading ? <div className="page-loader">正在读取资产…</div> : !assets.data?.length ? <EmptyState title="还没有资产" description="从开发者控制台启动 MCP、提交 Agent，或添加一个外部 MCP 地址。" action={<Button onClick={() => setOpen(true)}><Plus size={15}/>添加资产</Button>}/> : <div className="asset-grid">{assets.data.map(asset => {
      const verdict = assetVerdict(asset)
      return <Card className="asset-card" key={asset.id}>
      <div className="asset-card-head"><span className="asset-icon">{asset.kind === 'agent' ? <Bot/> : <Server/>}</span><div><div><strong>{asset.name}</strong><Badge tone={assetKindTone(asset)}>{assetKindLabel(asset)}</Badge>{asset.competition_id && <Badge tone="amber"><Trophy size={12} style={{verticalAlign:'-2px'}}/>{asset.region_name || asset.region_id || asset.competition_id}</Badge>}</div><small>{sourceLabel(asset.source_type)} · {asset.status}{asset.author_name && <span className="asset-author"> · <UserRound size={12} style={{verticalAlign:'-1px'}}/> {asset.author_name}</span>}</small></div></div>
      <p>{asset.description || '暂无资产说明'}</p>
      {asset.competition_id && <div className={`asset-score-block${verdict ? ` is-${verdict.kind}` : ' is-pending'}`}>{verdict ? verdict.kind === 'qualified' ? <CheckCircle2 size={14}/> : verdict.kind === 'superseded' ? <ClipboardList size={14}/> : <XCircle size={14}/> : <ClipboardList size={14}/>} {verdict ? <Badge tone={verdict.tone}>{verdict.text}</Badge> : <span className="asset-score-pending">{scoreStatusText(asset.scoring_status)}</span>}</div>}
      <div className="asset-actions"><Button variant="secondary" onClick={() => setDetailId(asset.id)}><Eye size={15}/>详情</Button><Button loading={syncing === asset.id} disabled={!asset.mixer_sync_eligible} title={asset.mixer_sync_block_reason || '同步到 Mixer'} onClick={() => sync(asset)}><CloudUpload size={15}/>{asset.latest_sync ? '再次同步' : '同步 Mixer'}</Button><Button variant="danger" loading={deleting === asset.id} title="删除资产" onClick={() => removeAsset(asset)}><Trash2 size={15}/>删除</Button></div>
      {!asset.mixer_sync_eligible && <small className="asset-block-reason">{asset.mixer_sync_block_reason}</small>}
      {asset.latest_sync && <div className="asset-sync-state"><RefreshCw size={13}/><span>v{asset.latest_sync.source_version} · {asset.latest_sync.status}</span></div>}
    </Card>})}</div>}
    <AssetModal open={open} close={() => setOpen(false)} done={() => { setOpen(false); void qc.invalidateQueries({ queryKey: ['assets'] }) }}/>
    <AssetDetailModal assetId={detailId} close={() => setDetailId(null)} notify={setNotice} onDelete={removeAsset} deleting={deleting}/>
  </div>
}

function AssetDetailModal({ assetId, close, notify, onDelete, deleting }: { assetId: string | null; close: () => void; notify: (message: string) => void; onDelete: (asset: AgentAsset) => void; deleting: string | null }) {
  const qc = useQueryClient()
  const detail = useQuery({
    queryKey: ['asset-detail', assetId],
    queryFn: () => api<AgentAssetDetail>(`/api/v1/assets/${assetId}`),
    enabled: !!assetId,
  })
  const [downloading, setDownloading] = useState(false)
  const [reparsing, setReparsing] = useState(false)
  const [error, setError] = useState('')
  const asset = detail.data
  const verdict = asset ? assetVerdict(asset) : null
  const submission = asset?.submission
  const sourceArtifact = submission?.artifacts.find(item => item.kind === 'source_code_zip')

  async function download() {
    if (!assetId) return
    setDownloading(true); setError('')
    try {
      const info = await api<AssetDownloadInfo>(`/api/v1/assets/${assetId}/download`)
      // 短时效签名 URL，直接触发浏览器下载，不经过网关中转。
      const anchor = document.createElement('a')
      anchor.href = info.download_url
      anchor.download = info.filename
      anchor.rel = 'noopener'
      anchor.click()
    } catch (reason) { setError(reason instanceof Error ? reason.message : '下载失败') } finally { setDownloading(false) }
  }

  async function reparse() {
    if (!assetId) return
    setReparsing(true); setError('')
    try {
      const result = await api<{ parse_status: string; message: string }>(`/api/v1/assets/${assetId}/reparse`, { method: 'POST' })
      notify(result.message)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['asset-detail', assetId] }),
        qc.invalidateQueries({ queryKey: ['assets'] }),
      ])
    } catch (reason) { setError(reason instanceof Error ? reason.message : '重新识别失败') } finally { setReparsing(false) }
  }

  return <Modal open={!!assetId} onOpenChange={value => !value && close()} title="资产详情" description="查看上传时填写的信息、上传的包和识别结论。" className="asset-detail-dialog">
    {detail.isLoading ? <div className="page-loader">正在读取详情…</div> : !asset ? <div className="form-error">{detail.error instanceof Error ? detail.error.message : '读取失败'}</div> : <div className="asset-detail">
      <header className="asset-detail-hero">
        <span className="asset-icon">{asset.kind === 'agent' ? <Bot/> : <Server/>}</span>
        <div className="asset-detail-hero-main">
          <div className="asset-detail-hero-name"><strong>{asset.name}</strong><Badge tone={assetKindTone(asset)}>{assetKindLabel(asset)}</Badge><Badge tone={asset.status === 'ready' ? 'green' : 'neutral'}>{statusLabel(asset.status)}</Badge>{asset.competition_id && verdict && <Badge tone={verdict.tone}>{verdict.text}</Badge>}</div>
          <small>{sourceLabel(asset.source_type)} · 创建于 {new Date(asset.created_at).toLocaleString()}{typeof asset.tool_count === 'number' ? ` · ${asset.tool_count} 个工具` : ''}{asset.competition_id && !verdict ? ` · ${scoreStatusText(asset.scoring_status)}` : ''}</small>
          {asset.description && <p>{asset.description}</p>}
        </div>
      </header>
      {asset.mcp_url && <CopyableCode label="MCP 地址" value={asset.mcp_url}/>}
      {submission ? <>
        <DetailSection icon={<ClipboardList size={15}/>} title="上传填写信息" aside={<Badge tone={submission.submission_type === 'competition' ? 'amber' : 'blue'}>{`v${submission.version} · ${submission.submission_type === 'competition' ? '赛事投稿' : '常规上传'}`}</Badge>}>
          <div className="asset-detail-grid">
            <DetailRow label="Agent 名称" value={submission.agent_name}/>
            <DetailRow label="团队" value={submission.team_name}/>
            <DetailRow label="队长" value={submission.captain_name}/>
            <DetailRow label="队长微信" value={submission.captain_wechat}/>
            {asset.competition_id && <DetailRow label="比赛" value={asset.region_name ? `${asset.region_name}（${asset.competition_id}${asset.region_id ? ` / ${asset.region_id}` : ''}）` : `${asset.competition_id}${asset.region_id ? ` / ${asset.region_id}` : ''}`}/>}
            {submission.submitted_at && <DetailRow label="提交时间" value={new Date(submission.submitted_at).toLocaleString()}/>}
            {submission.agent_experience_url && <DetailRow label="体验地址" value={submission.agent_experience_url} mono full/>}
            {submission.self_hosted_mcp_url && <DetailRow label="自托管 MCP" value={submission.self_hosted_mcp_url} mono full/>}
          </div>
          <DetailProse label="简介" value={submission.introduction}/>
          <DetailProse label="使用说明" value={submission.usage_instructions}/>
        </DetailSection>
        <DetailSection icon={<FileArchive size={15}/>} title="上传的包" accent="blue">
          {submission.artifacts.length ? <div className="asset-file-list">{submission.artifacts.map(item => <div className="asset-file-row" key={item.kind}>
            <span className="asset-file-icon">{item.kind === 'source_code_zip' ? <FileArchive size={17}/> : item.kind === 'avatar' ? <ImageIcon size={17}/> : <FileText size={17}/>}</span>
            <div className="asset-file-meta"><strong>{item.filename}</strong><small>{item.kind === 'source_code_zip' ? '源码包' : item.kind === 'avatar' ? '头像' : item.kind} · {formatBytes(item.size)}{item.uploaded_at ? ` · ${new Date(item.uploaded_at).toLocaleString()}` : ''}</small></div>
            {item.kind === 'source_code_zip' && <Button variant="secondary" loading={downloading} title="下载上传的源码包" onClick={download}><Download size={15}/>下载</Button>}
          </div>)}</div> : <small className="asset-detail-empty">没有已上传完成的文件</small>}
        </DetailSection>
        <DetailSection icon={<ScanSearch size={15}/>} title="识别结论" accent="green" aside={<Badge tone={submission.parse_status === 'completed' ? 'green' : submission.parse_status === 'failed' ? 'red' : submission.parse_status === 'running' ? 'blue' : 'neutral'}>{parseStatusLabel(submission.parse_status)}</Badge>}>
          {submission.parse_error && <div className="form-error">{submission.parse_error}</div>}
          {submission.parse_result ? <>
            <div className="asset-detail-grid">
              <DetailRow label="项目类型" value={submission.parse_result.project_type || 'unknown'}/>
              {submission.parse_result.entry_file && <DetailRow label="入口文件" value={submission.parse_result.entry_file} mono/>}
              {!!submission.parse_result.languages?.length && <DetailRow label="语言" value={submission.parse_result.languages.join('、')}/>}
              <DetailRow label="识别到的工具" value={`${submission.parse_result.tool_count ?? 0} 个`}/>
              {submission.parse_completed_at && <DetailRow label="识别完成" value={new Date(submission.parse_completed_at).toLocaleString()}/>}
            </div>
            {!!submission.parse_result.tools?.length && <div className="asset-tool-chips">{submission.parse_result.tools.map(tool => <span className="asset-tool-chip" key={tool.name} title={tool.description || tool.name}><code>{tool.name}</code>{tool.description && <small>{tool.description}</small>}</span>)}</div>}
          </> : !submission.parse_error && <small className="asset-detail-empty">{submission.parse_status === 'running' ? '识别正在进行中，稍后刷新查看结论。' : '还没有识别结论，可点击下方「重新识别」。'}</small>}
        </DetailSection>
        {error && <div className="form-error">{error}</div>}
      </> : <small className="asset-detail-empty">该资产为{sourceLabel(asset.source_type)}，没有上传的包，仅可查看基本信息。</small>}
      <div className="asset-detail-footer">
        {submission && <>
          <Button variant="secondary" loading={downloading} disabled={!sourceArtifact} title={sourceArtifact ? '下载上传的源码包' : '没有已上传完成的源码包'} onClick={download}><Download size={15}/>下载包</Button>
          <Button loading={reparsing} disabled={submission.parse_status === 'running' || !sourceArtifact} title={submission.parse_status === 'running' ? '识别正在进行中' : '重新识别上传的包并保存结论'} onClick={reparse}><ScanSearch size={15}/>重新识别</Button>
        </>}
        <Button className="asset-footer-danger" variant="danger" loading={deleting === asset.id} title="永久删除该资产及其上传的包和评分记录" onClick={() => onDelete(asset)}><Trash2 size={15}/>删除资产</Button>
      </div>
    </div>}
  </Modal>
}

function DetailSection({ icon, title, aside, accent, children }: { icon: ReactNode; title: string; aside?: ReactNode; accent?: 'blue' | 'green'; children: ReactNode }) {
  const className = accent ? `asset-detail-section is-accent-${accent}` : 'asset-detail-section'
  return <section className={className}>
    <header><span className="asset-detail-section-icon">{icon}</span><h3>{title}</h3>{aside && <div className="asset-detail-section-aside">{aside}</div>}</header>
    <div className="asset-detail-section-body">{children}</div>
  </section>
}

function DetailRow({ label, value, mono, full }: { label: string; value: string; mono?: boolean; full?: boolean }) {
  return <div className={full ? 'asset-detail-row is-full' : 'asset-detail-row'}><small>{label}</small>{mono ? <code>{value}</code> : <span>{value}</span>}</div>
}

function DetailProse({ label, value }: { label: string; value: string }) {
  return <div className="asset-detail-prose"><small>{label}</small><p>{value}</p></div>
}

function CopyableCode({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* 剪贴板不可用时忽略 */ }
  }
  return <div className="asset-copy-code">
    <small>{label}</small><code title={value}>{value}</code>
    <button type="button" className="icon-button" title={copied ? '已复制' : '复制'} onClick={copy}>{copied ? <Check size={14}/> : <Copy size={14}/>}</button>
  </div>
}

function formatBytes(size: number) {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${size} B`
}

function parseStatusLabel(status: string) { return ({ pending: '等待识别', running: '识别中', completed: '识别完成', failed: '识别失败' } as Record<string, string>)[status] || status }

function statusLabel(status: string) { return ({ ready: '就绪', pending: '待处理', provisioning: '部署中', failed: '异常', stopped: '已停止' } as Record<string, string>)[status] || status }

function AssetModal({ open, close, done }: { open: boolean; close: () => void; done: () => void }) {
  const [name, setName] = useState(''); const [url, setUrl] = useState(''); const [description, setDescription] = useState(''); const [kind, setKind] = useState<'agent' | 'mcp_server'>('mcp_server'); const [busy, setBusy] = useState(false); const [error, setError] = useState('')
  async function submit(event: FormEvent) { event.preventDefault(); setBusy(true); setError(''); try { await api('/api/v1/assets', { method: 'POST', body: { name, mcp_url: url, description: description || null, kind, transport: 'streamable-http' } }); done() } catch (reason) { setError(reason instanceof Error ? reason.message : '创建失败') } finally { setBusy(false) } }
  return <Modal open={open} onOpenChange={value => !value && close()} title="添加 Agent/MCP 资产" description="登记一个可访问的外部 MCP 地址，随后可直接创建评测。选择 Agent 类型才能同步到 Mixer。"><form className="stack" onSubmit={submit}><Field label="资产名称" required><Input value={name} onChange={event => setName(event.target.value)} required/></Field><Field label="资产类型"><select className="input" value={kind} onChange={event => setKind(event.target.value as 'agent' | 'mcp_server')}><option value="mcp_server">MCP Server</option><option value="agent">Agent</option></select></Field><Field label="MCP URL" required><Input value={url} onChange={event => setUrl(event.target.value)} placeholder="https://example.com/mcp/..." required/></Field><Field label="说明"><Textarea value={description} onChange={event => setDescription(event.target.value)}/></Field>{error && <div className="form-error">{error}</div>}<Button type="submit" loading={busy}>添加资产</Button></form></Modal>
}

function sourceLabel(source: string) { return ({ console: '开发者控制台', competition: '赛事投稿', regular_upload: '常规上传', external: '外部地址', eval_manual: '评测创建' } as Record<string, string>)[source] || source }

// A user upload is a submitted work, not a runnable Agent — so the badge
// reflects the actual source instead of the kind dichotomy (Agent vs MCP).
// kind stays the data-level marker for Mixer sync eligibility.
function assetKindLabel(asset: { kind: string; source_type: string }) {
  if (asset.source_type === 'competition') return '赛事作品'
  if (asset.source_type === 'regular_upload') return '常规作品'
  if (asset.source_type === 'console') return 'MCP 服务'
  if (asset.source_type === 'external') return '外部 MCP'
  return asset.kind === 'agent' ? 'Agent' : 'MCP'
}
function assetKindTone(asset: { source_type: string }): 'amber' | 'blue' | 'neutral' {
  if (asset.source_type === 'competition' || asset.source_type === 'regular_upload') return 'amber'
  if (asset.source_type === 'console') return 'blue'
  return 'neutral'
}

// Competition assets show a permanent score block. Before the region freezes
// the score is gated server-side, so we surface a friendly progress hint keyed
// off the (already-masked) scoring_status instead of a raw score.
function scoreStatusText(status?: string): string {
  if (!status) return '评分进行中，请耐心等待'
  const map: Record<string, string> = {
    pending_parse: '排队解析中，请耐心等待',
    parsing: '正在解析作品，请耐心等待',
    static_running: '静态评分中，请耐心等待',
    dynamic_running: '动态评分中，请耐心等待',
    pending_publish: '评分已完成，等待赛区统一公布',
    score_failed: '评分未通过，请关注后续通知',
    parse_failed: '作品解析失败，请关注后续通知',
    static_failed: '静态评审未通过，请关注后续通知',
    dynamic_failed: '动态评测未通过，请关注后续通知',
    provision_failed: '运行环境准备失败，请关注后续通知',
    frozen: '评分公布中',
  }
  return map[status] || '评分进行中，请耐心等待'
}

// 选手端不展示评分和排名，赛事作品仅呈现已发布的真实结果；
// 结论未定时返回 null，由调用方回退到进度提示文本。
function assetVerdict(asset: AgentAsset): { text: string; tone: 'green' | 'red' | 'neutral'; kind: 'qualified' | 'not-qualified' | 'technical-failed' | 'superseded' } | null {
  if (asset.result_status === 'superseded') return { text: '已被最新投稿替代', tone: 'neutral', kind: 'superseded' }
  if (asset.result_published && asset.result_status === 'qualified') return { text: '晋级', tone: 'green', kind: 'qualified' }
  if (asset.result_published && asset.result_status === 'not_qualified') return { text: '未晋级', tone: 'red', kind: 'not-qualified' }
  if (asset.result_published && ['parse_failed', 'technical_failed'].includes(asset.result_status || '')) return { text: '作品处理异常', tone: 'red', kind: 'technical-failed' }
  const failed = ['parse_failed', 'static_failed', 'dynamic_failed', 'provision_failed', 'score_failed']
  if (asset.scoring_status && failed.includes(asset.scoring_status)) return { text: '作品处理异常', tone: 'red', kind: 'technical-failed' }
  return null
}
